import { CircleDot, XCircle, CheckCircle2, LayoutList } from "lucide-react";
import { Tag, Badge } from "antd";
import dayjs from "dayjs";
import useAuthStore from "stores/AuthStore/AuthStore";
import { formatDifferenceDuration } from "commons/helper";

export const TICKET_STATUS = {
  OPEN: "OPEN",
  CLOSED: "CLOSED",
  RESOLVED: "RESOLVED",
};

export const STATUS_TAG = {
  OPEN: { color: "blue", icon: <CircleDot className="h-3 w-3" /> },
  CLOSED: { color: "default", icon: <XCircle className="h-3 w-3" /> },
  RESOLVED: { color: "success", icon: <CheckCircle2 className="h-3 w-3" /> },
};

export const PAGE_SIZE = 10;

/**
 * @param {{ onSelect: (row: object) => void }} callbacks
 */
export function getTicketColumns({ onSelect }) {
  return [
    {
      title: "Ticket ID",
      key: "ticket_id",
      width: 120,
      render: (_, row) => (
        <span className="text-xs text-muted-foreground">{row.ticket_id}</span>
      ),
    },
    {
      title: "Subject",
      key: "subject",
      render: (_, row) => (
        <button
          className="text-left hover:text-primary transition-colors"
          onClick={() => onSelect(row)}
        >
          <p className="font-medium text-sm line-clamp-1">
            {row.data?.subject ?? "—"}
          </p>
          <p className="text-xs text-muted-foreground line-clamp-1 mt-0.5">
            {row.data?.body ?? ""}
          </p>
        </button>
      ),
    },
    {
      title: "Status",
      key: "status",
      width: 120,
      render: (_, row) => {
        const tag = STATUS_TAG[row.status] ?? STATUS_TAG.OPEN;
        return (
          <Tag
            color={tag.color}
            className="flex items-center gap-1 w-fit text-xs font-semibold uppercase"
          >
            {tag.icon} {row.status}
          </Tag>
        );
      },
    },
    {
      title: "Response",
      key: "resolution",
      width: 100,
      render: (_, row) =>
        row.data?.resolution ? (
          <Badge
            status="success"
            text={
              <span className="text-xs text-emerald-600 font-medium">
                Replied
              </span>
            }
          />
        ) : (
          <Badge
            status="processing"
            text={
              <span className="text-xs text-muted-foreground">Pending</span>
            }
          />
        ),
    },
    {
      title: "Raised",
      key: "created_at",
      width: 140,
      render: (_, row) =>
        row.audit?.created_at ? (
          <span className="text-xs text-muted-foreground">
            {dayjs(row.audit.created_at).format("MMM D, YYYY")}
          </span>
        ) : (
          "—"
        ),
    },
    {
      title: "TAT",
      key: "tat",
      width: 120,
      render: (row) => (
        <span className="text-xs text-muted-foreground">
          {formatDifferenceDuration(row.audit.created_at, row.audit.updated_at)}
        </span>
      ),
    },
  ];
}

/**
 * @param {{ open: number, resolved: number, closed: number }} counts
 */
export function getTicketStatCards(counts) {
  return [
    {
      label: "Total",
      value: counts.total ?? 0,
      color: "text-foreground",
      bg: "bg-card",
      iconBg: "bg-slate-100",
      iconColor: "text-slate-500",
      icon: <LayoutList className="h-5 w-5" />,
    },
    {
      label: "Open",
      value: counts.open ?? 0,
      color: "text-blue-600",
      bg: "bg-blue-50/30",
      iconBg: "bg-blue-100",
      iconColor: "text-blue-600",
      icon: <CircleDot className="h-5 w-5" />,
    },
    {
      label: "Resolved",
      value: counts.resolved ?? 0,
      color: "text-emerald-600",
      bg: "bg-emerald-50/30",
      iconBg: "bg-emerald-100",
      iconColor: "text-emerald-600",
      icon: <CheckCircle2 className="h-5 w-5" />,
    },
    {
      label: "Closed",
      value: counts.closed ?? 0,
      color: "text-muted-foreground",
      bg: "bg-muted/30",
      iconBg: "bg-slate-100",
      iconColor: "text-slate-400",
      icon: <XCircle className="h-5 w-5" />,
    },
  ];
}

export const Faqs = () => {
  const { isRootUser } = useAuthStore();
  return [
    {
      q: "How do I check my application status?",
      a: "After submitting your partner application you will receive a link with your Account ID. Visit the Application Status page to track progress — statuses include Pending (under review), Verified (approved and active), and Rejected (with a reason provided). Once verified, click 'Go to Dashboard' to log in and start using the portal.",
    },
    {
      q: "My application was rejected. What should I do?",
      a: "If your application is rejected, the reason is shown on the Application Status page. Review the feedback, address the concerns raised, and reach out to our support team for specific guidance before re-applying.",
    },
    {
      q: "How do I create an event?",
      a: "Go to Events → Create Event. The wizard takes you through 6 steps: Event Details (title, type, access), Date & Location (venue, timings), Content & Extras (amenities, activities, organiser info, FAQ, schedule, T&C), Tickets (pricing tiers and contact info), Media (photos/videos), and a final Review before submitting. Events are saved as drafts and can be published from the Event Detail page.",
    },
    {
      q: "Can I edit an event after creating it?",
      a: "Only draft events can be edited. Open the event from the Events list and click Edit to make changes. Published events are locked to protect data integrity.",
    },
    ...(isRootUser
      ? [
          {
            q: "How do membership tiers and plans work?",
            a: "Go to Memberships and select a community. Under the Tiers & Plans tab, create a tier (e.g. Gold, Silver) and add one or more pricing plans to it. Each plan has a billing type (Free or Subscription), price, and duration (Monthly = 30 days, Annually = 365 days). Members of that community can then subscribe to the plan.",
          },
        ]
      : []),
    {
      q: "How do I view and manage orders and bookings?",
      a: "Go to Orders & Bookings. You can filter by community or view across all communities at once. The Orders tab shows ticket purchases and the Bookings tab shows membership bookings. Click any order or booking to see its full detail page.",
    },
    ...(isRootUser
      ? [
          {
            q: "How do I manage my community settings?",
            a: "Go to Communities, click Manage on a community card, then open the Settings tab. You can edit all community details including name, description, locations, contact info, owner metadata, activities, and terms & conditions. Changes are saved immediately on clicking Save changes.",
          },
        ]
      : []),
    {
      q: "What does the Dashboard show?",
      a: "The Dashboard shows a summary of your account: total events, total community members, active subscriptions, and revenue metrics. It also displays upcoming events with their status and a revenue growth chart. From here you can quickly jump to create an event or manage memberships.",
    },
    {
      q: "How do I update my profile?",
      a: "Go to Settings → Profile to update your account details. The Security tab is coming soon and will allow password and security option management.",
    },
  ];
};
